import React, { useEffect, useRef, useState } from "react";
import {
  PaperClipIcon,
  PaperAirplaneIcon,
  CameraIcon,
  VideoCameraIcon,
  MicrophoneIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import io from "socket.io-client";
import { RiSpeakLine } from "react-icons/ri";
import html2canvas from "html2canvas";
import FeedbackForm from "./FeedbackForm";
import "../CSS/Message.css";
import RenderAttachment from "./attachmentRenders/RenderAttachment";
import Feedback from "./Feedback";
import DOMPurify from "dompurify";
import axios from "axios";

const socket = io(`${process.env.REACT_APP_CHAT_SERVER}`);

const Message = ({
  setMessage,
  message,
  setAttachment,
  setAttachmentType,
  sendMessage,
  isOpen,
  goBack,
  selectedConversation,
  setSelectedConversation,
  setConversations,
  setupDetails,
  setView,
  widgetConfigs,
}) => {
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recognitionRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [activeTab, setActiveTab] = useState("Discussion"); // Default to "Discussion"

  useEffect(() => {
    const scrollToBottom = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    };

    // Trigger scroll to bottom when messages change
    scrollToBottom();
  }, [selectedConversation?.messages]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachment(reader.result);
        setAttachmentType(file.type);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeQuotedText = (message) => {
    const quotedTextPattern =
      /(?:^On .+ wrote:|^From: .+|^\s*>.*(?:\n|$)|^>.*(?:\n|$))/gms;
    return message.replace(quotedTextPattern, "").trim();
  };

  const handleIconClick = () => {
    fileInputRef.current.click();
  };

  const captureScreenshot = async () => {
    try {
      const chatWindow = document.querySelector(".dexkor-chat-widget");

      if (chatWindow) {
        chatWindow.style.visibility = "hidden"; // Hide the chat window
      }

      // Wait for the next animation frame to ensure the DOM is updated
      await new Promise((resolve) => requestAnimationFrame(resolve));

      // Create a canvas element to capture the current viewport
      const canvas = await html2canvas(document.body, {
        x: window.scrollX, // Offset for horizontal scroll
        y: window.scrollY, // Offset for vertical scroll
        width: window.innerWidth, // Width of the viewport
        height: window.innerHeight, // Height of the viewport
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        useCORS: true, // Enable CORS to handle cross-origin images
      });

      // Convert the canvas to a data URL (image)
      const screenshot = canvas.toDataURL("image/png");

      if (chatWindow) {
        chatWindow.style.visibility = ""; // Restore the chat window
      }

      setAttachment(screenshot);
      setAttachmentType("image/png");
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    }
  };

  const startScreenRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: "video/webm; codecs=vp9",
      });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "video/webm" });
        const reader = new FileReader();
        let result;
        reader.onloadend = () => {
          setAttachment(reader.result);
          setAttachmentType("video/webm");
        };
        reader.readAsDataURL(blob);
        audioChunksRef.current = [];
      };

      mediaRecorderRef.current.start();

      setTimeout(() => {
        mediaRecorderRef.current.stop();
        stream.getTracks().forEach((track) => track.stop());
      }, 8000); // Stop recording after 8 seconds
    } catch (error) {
      console.error("Error starting screen recording:", error);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = () => {
          setAttachment(reader.result);
          setAttachmentType("audio/webm");
        };
        reader.readAsDataURL(blob);
        audioChunksRef.current = [];
      };

      mediaRecorderRef.current.start();
      setRecording(true);
    } catch (error) {
      console.error("Error starting audio recording:", error);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  let touchTimer;

  const startTranscription = () => {
    if (!("webkitSpeechRecognition" in window)) {
      console.error("Speech recognition is not supported in this browser.");
      return;
    }

    recognitionRef.current = new window.webkitSpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = "en-US";

    let finalTranscript = "";

    recognitionRef.current.onresult = (event) => {
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }
      setMessage(finalTranscript + interimTranscript);
    };

    recognitionRef.current.start();
    setTranscribing(true);
  };

  const stopTranscription = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setTranscribing(false);
    }
  };

  const handleTouchStart = () => {
    // Clear any existing timer
    clearTimeout(touchTimer);

    // Start transcription with a slight delay
    touchTimer = setTimeout(() => {
      startTranscription();
    }, 100); // 100ms delay to ensure touch events are processed correctly
  };

  const handleTouchEnd = () => {
    // Clear the timer if touch ends before the delay
    clearTimeout(touchTimer);

    stopTranscription();
  };

  const handleTouchCancel = () => {
    stopTranscription();
  };
  const [customerDetails, setCustomerDetails] = useState(null);
  useEffect(() => {
    setCustomerDetails(
      JSON.parse(localStorage.getItem("dexkor-chat-widget-customer-details"))
    );
  }, []);

  const [ticketFormFields, setTicketFormFields] = useState(null);
  const [isFormFieldsLoading, setIsFormFieldsLoading] = useState(false);
  useEffect(() => {
    setIsFormFieldsLoading(true);
    const getTicketForm = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/widget/ticket-form/${widgetConfigs.uniquePartnerId}`
        );
        const formFields = response?.data?.data?.fields;
        const fieldsToSendInInitalFields = formFields.filter(
          (field) =>
            field.fieldType === "text" ||
            field.fieldType === "textarea" ||
            field.fieldType === "number" ||
            field.fieldType === "email" ||
            field.fieldType === "date"
        );
        setTicketFormFields(fieldsToSendInInitalFields);
        setIsFormFieldsLoading(false);
      } catch (error) {
        console.error("Error fetching form config:", error);
        setIsFormFieldsLoading(false);
      }
    };
    getTicketForm();
  }, [widgetConfigs.uniquePartnerId]);

  useEffect(() => {
    const sendDefaultMessages = async () => {
      ticketFormFields?.map((field) => {
        const ticketFieldLabel = field?.label;
        const ticketFieldName = field?.name;
        if (
          selectedConversation ||
          selectedConversation?.ticketId?.customFields?.[ticketFieldName]
        ) {
          const msg = {
            room: selectedConversation.roomId,
            sender: "Customer",
            senderId: customerDetails._id,
            message: `${ticketFieldLabel}: ${selectedConversation?.ticketId?.customFields?.[ticketFieldName]}`,
            attachment: null,
            attachmentType: null,
          };
          socket.emit("message", msg);
        }
      });

      // if (selectedConversation || selectedConversation?.ticketId?.subject) {
      //   const msg = {
      //     room: selectedConversation.roomId,
      //     sender: "Customer",
      //     senderId: customerDetails._id,
      //     message: `Subject: ${selectedConversation?.ticketId.subject}`,
      //     attachment: null,
      //     attachmentType: null,
      //   };
      //   socket.emit("message", msg);
      // }

      // if (selectedConversation || selectedConversation?.ticketId?.description) {
      //   const msg = {
      //     room: selectedConversation.roomId,
      //     sender: "Customer",
      //     senderId: customerDetails._id,
      //     message: `Description: ${selectedConversation?.ticketId?.description}`,
      //     attachment: null,
      //     attachmentType: null,
      //   };
      //   socket.emit("message", msg);
      // }

      // if (selectedConversation || selectedConversation?.ticketId?.email) {
      //   const msg = {
      //     room: selectedConversation.roomId,
      //     sender: "Customer",
      //     senderId: customerDetails._id,
      //     message: `Email: ${selectedConversation?.ticketId.email}`,
      //     attachment: null,
      //     attachmentType: null,
      //   };
      //   socket.emit("message", msg);
      // }
    };
    if (selectedConversation && selectedConversation?.messages?.length === 0) {
      if (customerDetails && ticketFormFields) {
        sendDefaultMessages();
      }
    }
  }, [selectedConversation, customerDetails, ticketFormFields]);

  return (
    <>
      {isOpen && (
        <div className="flex flex-col h-full mb-0 mt-0 overflow-hidden">
          <div className="flex items-center justify-start z-10">
            <button
              onClick={goBack}
              className="dexkor-ticket-back-button p-1 h-7 w-7 rounded-md mr-2 mb-5 z-10"
            >
              <ChevronLeftIcon className="h-5 cursor-pointer" />
            </button>
            <div className="text-left">
              <p className="font-bold text-white">
                {selectedConversation?.ticketId?.ticketNumber}
              </p>
              {selectedConversation && (
                <div className="flex items-center justify-start cursor-pointer z-10 text-sm">
                  <div className="mr-2 mb-1 text-white">
                    Status:{" "}
                    <span>{selectedConversation?.ticketId?.status}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex p-2 gap-x-6">
            {/* Discussion Tab */}
            <div
              className={`text-sm cursor-pointer ${
                activeTab === "Discussion"
                  ? "text-blue-600 font-semibold"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("Discussion")}
            >
              Discussion
            </div>

            {/* Details Tab */}
            <div
              className={`text-sm cursor-pointer ${
                activeTab === "Details"
                  ? "text-blue-600 font-semibold"
                  : "text-gray-600"
              }`}
              onClick={() => {
                setActiveTab("Details");
                setView("MessageDetails");
              }}
            >
              Details
            </div>
          </div>
          <hr className="mt-0 border-t border-gray-300"></hr>

          {/* message field starts */}
          {
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 dexkor-scrollbar-hidden-chat"
            >
              {selectedConversation.messages.map(
                (msg, index) =>
                  msg.type !== "Internal" && (
                    <div
                      key={index}
                      className={`flex items-${
                        msg.sender === "Customer" ? "end" : "start"
                      } ${
                        msg.sender === "Customer" ? "justify-end ml-auto" : ""
                      }`}
                    >
                      <div className="block">
                        {msg.attachment && (
                          <div
                            className={`${
                              msg.sender === "Customer"
                                ? "dexkor-customer-attachment"
                                : "dexkor-agent-attachment"
                            } ${
                              msg.attachmentType.includes("audio")
                                ? "rounded-full"
                                : "rounded-lg"
                            }`}
                          >
                            <RenderAttachment
                              attachment={msg.attachment}
                              attachmentType={msg.attachmentType}
                            />
                          </div>
                        )}
                        {msg.message && (
                          <div
                            className={`${
                              msg.sender === "Customer"
                                ? "dexkor-customer-message-box"
                                : "dexkor-agent-message-box"
                            }`}
                          >
                            <p
                              className={`${
                                msg.sender === "Customer"
                                  ? "dexkor-customer-message-text"
                                  : "dexkor-agent-message-text"
                              }`}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  removeQuotedText(msg.message)
                                ),
                              }}
                            ></p>
                          </div>
                        )}
                        <p
                          className={`${
                            msg.sender === "Customer"
                              ? "dexkor-customer-timestamp"
                              : "dexkor-agent-timestamp "
                          }`}
                        >
                          {new Date(msg.timestamp).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </p>
                      </div>
                    </div>
                  )
              )}

              {/* Feedback form starts */}
              {selectedConversation?.ticketId?.status === "resolved" && (
                <FeedbackForm
                  selectedConversation={selectedConversation}
                  setSelectedConversation={setSelectedConversation}
                  setConversations={setConversations}
                />
              )}

              {/* Feedback starts */}
              {selectedConversation?.ticketId?.status === "closed" && (
                <Feedback selectedConversation={selectedConversation} />
              )}
            </div>
          }

          {/* input fields starts */}
          {selectedConversation?.ticketId?.status !== "closed" &&
            selectedConversation?.ticketId?.status !== "resolved" && (
              <div
                className="dexkor-message-input-container p-0 border-gray-200 rounded-lg"
                style={{
                  border: `1px solid ${
                    setupDetails?.widgetCustomizations.primaryColor || "#4C73DE"
                  }`,
                }}
              >
                <div
                  className="dexkor-message-input-options relative flex items-center p-1"
                  style={{
                    borderBottom: `1px solid ${
                      setupDetails?.widgetCustomizations.primaryColor ||
                      "#4C73DE"
                    }`,
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={handleIconClick}
                    className="text-gray-500 hover:text-gray-700 ml-3"
                  >
                    <PaperClipIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={captureScreenshot}
                    className="text-gray-500 hover:text-gray-700 ml-3"
                  >
                    <CameraIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => startScreenRecording()}
                    className="text-gray-500 hover:text-gray-700 ml-3"
                  >
                    <VideoCameraIcon className="h-4 w-4" />
                  </button>
                </div>
                <input
                  placeholder="Type/record your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                  className="flex-1 mt-1 w-full h-auto py-2 px-3 mb-3 border-none text-sm focus:outline-none focus:ring-0  block bg-gray-100"
                  rows={1}
                />
                <div className="flex justify-end p-1">
                  <button
                    className={`bg-transparent pl-0 text-gray-500 ${
                      transcribing
                        ? "hover:text-red-500"
                        : "hover:text-gray-700"
                    } ${transcribing ? "text-red-500" : ""}`}
                    onMouseDown={startTranscription}
                    onMouseUp={stopTranscription}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchCancel}
                  >
                    <RiSpeakLine className="h-4 w-4" />
                  </button>
                  <button
                    className={`text-gray-500 ${
                      recording ? "hover:text-green-500" : "hover:text-gray-700"
                    } ml-2 ${recording ? "text-green-500" : ""}`}
                    onClick={recording ? stopRecording : startRecording}
                  >
                    <MicrophoneIcon
                      className={`h-4 w-4 ${
                        recording ? "animate-pulse" : "text-gray-500"
                      }`}
                    />
                  </button>
                  <button
                    className="bg-blue-500 text-white p-2 rounded-full ml-2 hover:bg-blue-600"
                    style={{
                      background: `${
                        setupDetails?.widgetCustomizations.primaryColor ||
                        "#4C73DE"
                      }`,
                    }}
                    onClick={sendMessage}
                  >
                    <PaperAirplaneIcon className="h-2 w-2" />
                  </button>
                </div>
              </div>
            )}
        </div>
      )}
    </>
  );
};

export default Message;
